import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import AuroraBackground from "./AuroraBackground";

/**
 * WelcomeSplash — premium full-screen "wow" moment shown immediately after a
 * successful login. Renders a brief 2.4s animated greeting with the user's
 * first name (letter-by-letter reveal) over an aurora background, then
 * smoothly fades away. Auto-dismisses + cannot be retriggered in the same tab.
 *
 * Trigger: read `sessionStorage.prosperr_show_welcome === '1'` on mount.
 *  - Set by Login.jsx right before navigating to /dashboard.
 *  - Cleared by this component as soon as it shows.
 */
export default function WelcomeSplash({ name = "" }) {
  const [show, setShow] = useState(false);
  const [phase, setPhase] = useState("enter"); // enter → reveal → exit

  const firstName = (name || "").split(" ")[0] || "Bienvenue";
  const letters = useMemo(() => Array.from(firstName), [firstName]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const armed = sessionStorage.getItem("prosperr_show_welcome");
    if (armed !== "1") return;
    sessionStorage.removeItem("prosperr_show_welcome");
    setShow(true);

    const t1 = setTimeout(() => setPhase("reveal"), 80);
    const t2 = setTimeout(() => setPhase("exit"), 2100);
    const t3 = setTimeout(() => setShow(false), 2700);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  if (!show || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{
        opacity: phase === "exit" ? 0 : 1,
        transition: "opacity 600ms cubic-bezier(.4,0,.2,1)",
        pointerEvents: phase === "exit" ? "none" : "auto",
      }}
      data-testid="welcome-splash"
    >
      <AuroraBackground intensity={1.2} />

      <div className="relative text-center px-6">
        {/* Wordmark */}
        <div
          className="text-[10.5px] font-bold uppercase tracking-[0.4em] text-emerald-300 mb-7 splash-fade-in"
          style={{ animationDelay: "120ms" }}
        >
          Prosperr · Copilote
        </div>

        {/* Greeting with letter-by-letter reveal */}
        <h1
          className="font-display text-5xl sm:text-6xl md:text-7xl font-semibold tracking-tight text-white leading-[1.05]"
          aria-label={`Bonjour ${firstName}`}
        >
          <span className="block opacity-80 splash-fade-up text-2xl sm:text-3xl font-normal text-zinc-300 mb-2" style={{ animationDelay: "260ms" }}>
            Bonjour
          </span>
          <span className="block">
            {letters.map((ch, i) => (
              <span
                key={`${ch}-${i}`}
                className="inline-block splash-letter"
                style={{
                  animationDelay: `${380 + i * 55}ms`,
                  whiteSpace: "pre",
                }}
              >
                {ch}
              </span>
            ))}
          </span>
        </h1>

        {/* Tagline */}
        <p
          className="mt-6 text-[14px] sm:text-base text-zinc-400 max-w-md mx-auto splash-fade-up"
          style={{ animationDelay: `${550 + letters.length * 55}ms` }}
        >
          Préparation de votre plan d'attaque…
        </p>

        {/* Progress bar */}
        <div
          className="mt-8 mx-auto h-[2px] w-48 bg-white/8 rounded-full overflow-hidden splash-fade-in"
          style={{ animationDelay: `${720 + letters.length * 55}ms` }}
        >
          <div className="h-full splash-progress" />
        </div>
      </div>

      <style>{`
        @keyframes splashFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes splashFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes splashLetter { 0% { opacity: 0; transform: translateY(28px) scale(0.94); filter: blur(8px); } 60% { filter: blur(0); } 100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); } }
        @keyframes splashProgress { 0% { width: 0; transform: translateX(0); } 60% { width: 100%; transform: translateX(0); } 100% { width: 100%; transform: translateX(0); } }
        .splash-fade-in { animation: splashFadeIn 600ms cubic-bezier(.2,.7,.3,1) both; }
        .splash-fade-up { animation: splashFadeUp 700ms cubic-bezier(.2,.7,.3,1) both; }
        .splash-letter { animation: splashLetter 700ms cubic-bezier(.2,.7,.3,1) both; transform-origin: 50% 100%; }
        .splash-progress { background: linear-gradient(90deg, transparent, rgba(0,220,130,1), rgba(217,70,239,1), transparent); animation: splashProgress 1800ms cubic-bezier(.4,0,.2,1) both; }
        @keyframes auroraDrift1 {
          0%, 100% { transform: translate(0,0) rotate(0); }
          50% { transform: translate(8vw, 6vh) rotate(8deg); }
        }
        @keyframes auroraDrift2 {
          0%, 100% { transform: translate(0,0) rotate(0); }
          50% { transform: translate(-10vw, 8vh) rotate(-6deg); }
        }
        @keyframes auroraDrift3 {
          0%, 100% { transform: translate(0,0); }
          50% { transform: translate(6vw, -8vh); }
        }
        @keyframes auroraDrift4 {
          0%, 100% { transform: translate(0,0); }
          50% { transform: translate(-6vw, 5vh); }
        }
      `}</style>
    </div>,
    document.body,
  );
}
